// src/stateCities/StateCityList.tsx

import React, { FC, useState, Fragment, useEffect } from 'react';
import {
    List,
    Datagrid,
    TextField,
    BooleanField,
    EditButton,
    useNotify,
    useDelete,
    ListProps,
    useRecordContext,
    ReferenceInput,
    SelectInput,
    TextInput,
    usePermissions,
    Button as RAButton,
    BulkDeleteButton,
    BulkExportButton,
    BulkActionProps,
} from 'react-admin';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import useAuthorize from '../hooks/useAuthorize';
import { UserRole } from '../types/types';

// same StateFilter as before
const StateFilter = [
    <ReferenceInput
        source="state_id"
        reference="states/getAllStates"
        alwaysOn
        key="stateFilter"
    >
        <SelectInput optionText="state_name" label="State" />
    </ReferenceInput>,
    <TextInput
        label="Search City"
        source="city_search"
        resettable
        alwaysOn
        key="citySearch"
    />,
];

// helper to map your authProvider perms into ['Admin','ProductAdmin',…]
const normalizeRoles = (perms: any): string[] => {
    const arr = Array.isArray(perms) ? perms : perms != null ? [perms] : [];
    return arr.map(p =>
        typeof p === 'number'
            ? (UserRole[p] as unknown as string)
            : String(p)
    );
};

// custom bulk action buttons
const MyBulkActionButtons: FC<BulkActionProps> = props => {
    const { permissions } = usePermissions<any>();
    const roles = normalizeRoles(permissions);
    const isAdmin = roles.includes('Admin');
    const isProductAdmin = roles.includes('ProductAdmin');

    const [openDenied, setOpenDenied] = useState(false);

    return (
        <Fragment>
            <BulkExportButton {...props} />
            {isAdmin ? (
                <BulkDeleteButton {...props} />
            ) : isProductAdmin ? (
                <>
                    <RAButton
                        label="Delete"
                        onClick={() => setOpenDenied(true)}
                        startIcon={<DeleteIcon />}
                    />
                    <Dialog open={openDenied} onClose={() => setOpenDenied(false)}>
                        <DialogTitle>Permission Denied</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                You don’t have permission to delete. Please contact your administrators.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setOpenDenied(false)}>OK</Button>
                        </DialogActions>
                    </Dialog>
                </>
            ) : null}
        </Fragment>
    );
};

const DeleteWithConfirmButton: FC = () => {
    const record = useRecordContext<{ id: string | number }>();
    const { permissions } = usePermissions<any>();
    const roles = normalizeRoles(permissions);
    const isAdmin = roles.includes('Admin');
    const isProductAdmin = roles.includes('ProductAdmin');

    const [openConfirm, setOpenConfirm] = useState(false);
    const [openDenied, setOpenDenied] = useState(false);
    const [deleteOne, { isLoading }] = useDelete();
    const notify = useNotify();

    const handleClick = () => {
        if (isAdmin) setOpenConfirm(true);
        else if (isProductAdmin) setOpenDenied(true);
    };

    const confirmDelete = async () => {
        await deleteOne('statecities', { id: record.id });
        notify('resources.statecities.notification.deleted', {
            type: 'info',
            messageArgs: { smart_count: 1, name: 'City' },
        });
        setOpenConfirm(false);
    };

    return (
        <Fragment>
            <RAButton
                label="Delete"
                onClick={handleClick}
                startIcon={<DeleteIcon />}
                disabled={isLoading}
            />

            <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this city?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirm(false)}>Cancel</Button>
                    <Button onClick={confirmDelete} color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openDenied} onClose={() => setOpenDenied(false)}>
                <DialogTitle>Permission Denied</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You don’t have permission to delete. Please contact your administrators.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDenied(false)}>OK</Button>
                </DialogActions>
            </Dialog>
        </Fragment>
    );
};

export const StateCityList: FC<ListProps> = props => {
    // only Admin / ProductAdmin can view
    useAuthorize([UserRole.Admin, UserRole.ProductAdmin]);

    return (
        <List
            filters={StateFilter}
            filterDefaultValues={{ active: true }}
            bulkActionButtons={<MyBulkActionButtons />}
            {...props}
        >
            <Datagrid>
                <TextField source="city_name" />
                <BooleanField source="active" label="Active" />
                <BooleanField source="app_active" label="Active on App" />
                <BooleanField source="show_local_products_only" label="Show Local Products Only" />
                <EditButton />
                <DeleteWithConfirmButton />
            </Datagrid>
        </List>
    );
};

// allow default imports too
export default StateCityList;
